import { lazy, Suspense, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { LoginPage } from "@/pages/auth/LoginPage";
import { ForgotPasswordPage } from "@/pages/auth/ForgotPasswordPage";
import { ResetPasswordPage } from "@/pages/auth/ResetPasswordPage";
import { ForcePasswordChangePage } from "@/pages/auth/ForcePasswordChangePage";
import { RequireAuth, RequireRole, RequirePasswordChanged } from "@/lib/auth/guards";
import { TenantLayout } from "@/components/TenantLayout";
import { PageSkeleton } from "@/components/PageState";

// Each route is its own JS chunk. These thunks are shared between lazy() (which
// downloads a chunk on first navigation) and the idle prefetch below (which
// warms every other chunk once the app is idle) so moving between modules feels
// instant instead of waiting ~1s to fetch the chunk on demand.
const load = {
  dashboard:        () => import("@/pages/tenant/DashboardPage").then((m) => ({ default: m.DashboardPage })),
  invoices:         () => import("@/pages/tenant/InvoicesPage").then((m) => ({ default: m.InvoicesPage })),
  notices:          () => import("@/pages/tenant/NoticesPage").then((m) => ({ default: m.NoticesPage })),
  profile:          () => import("@/pages/tenant/ProfilePage").then((m) => ({ default: m.ProfilePage })),
  documents:        () => import("@/pages/tenant/DocumentsPage").then((m) => ({ default: m.DocumentsPage })),
  electricity:      () => import("@/pages/tenant/ElectricityPage").then((m) => ({ default: m.ElectricityPage })),
  transactions:     () => import("@/pages/tenant/TransactionHistoryPage").then((m) => ({ default: m.TransactionHistoryPage })),
};

const DashboardPage          = lazy(load.dashboard);
const InvoicesPage           = lazy(load.invoices);
const NoticesPage            = lazy(load.notices);
const ProfilePage            = lazy(load.profile);
const DocumentsPage          = lazy(load.documents);
const ElectricityPage        = lazy(load.electricity);
const TransactionHistoryPage = lazy(load.transactions);

function usePrefetchRoutes() {
  useEffect(() => {
    const prefetch = () => Object.values(load).forEach((fn) => { void fn(); });
    const ric = (window as unknown as {
      requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number;
    }).requestIdleCallback;
    if (ric) {
      ric(prefetch, { timeout: 4000 });
    } else {
      const t = setTimeout(prefetch, 2000);
      return () => clearTimeout(t);
    }
  }, []);
}

export function App() {
  usePrefetchRoutes();
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      <Route
        path="/force-password-change"
        element={
          <RequireAuth>
            <ForcePasswordChangePage />
          </RequireAuth>
        }
      />

      <Route
        path="/"
        element={
          <RequireAuth>
            <RequireRole roles={["tenant"]}>
              <RequirePasswordChanged>
                <TenantLayout />
              </RequirePasswordChanged>
            </RequireRole>
          </RequireAuth>
        }
      >
        <Route index element={<Suspense fallback={<PageSkeleton />}><DashboardPage /></Suspense>} />
        <Route path="invoices" element={<Suspense fallback={<PageSkeleton />}><InvoicesPage /></Suspense>} />
        <Route path="notices" element={<Suspense fallback={<PageSkeleton />}><NoticesPage /></Suspense>} />
        <Route path="documents" element={<Suspense fallback={<PageSkeleton />}><DocumentsPage /></Suspense>} />
        <Route path="electricity" element={<Suspense fallback={<PageSkeleton />}><ElectricityPage /></Suspense>} />
        <Route path="transactions" element={<Suspense fallback={<PageSkeleton />}><TransactionHistoryPage /></Suspense>} />
        <Route path="profile" element={<Suspense fallback={<PageSkeleton />}><ProfilePage /></Suspense>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
